import { FC, ReactNode, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonSearchbar } from '@ionic/react';
import { Card } from '../components/Card';
import { Text } from '../components/Text';

const useStyles = createUseStyles({
    section: {
        display: 'flow-root',
        marginBottom: 10
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 16px 2px'
    },

    beaAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0
    },

    subhead: {
        fontSize: 13,
        lineHeight: 1.5,
        color: 'var(--ion-color-step-650)'
    },

    form: {
        padding: '6px 12px 0',
        // the rows below set their own bottom margin, and this keeps it inside the card rather than
        // letting it collapse out through a parent that has no bottom padding of its own
        display: 'flow-root'
    },

    fieldWrap: {
        position: 'relative'
    },

    // ion-searchbar exposes no border custom properties, so the outline has to be put on the input
    // it renders. Without it the field reads as a label rather than something to type in, which is
    // the wrong signal for the one control this card is built around.
    search: {
        '--box-shadow': 'none',
        '--background': 'var(--ion-background-color)',
        paddingLeft: 0,
        paddingRight: 0,

        '& .searchbar-input': {
            border: '1.5px solid var(--ion-color-step-200)',
            borderRadius: 10,
            fontSize: 15.5,
            // Ionic sizes this to exactly its line-height plus padding, which leaves no room for the
            // border once one is added and clips the descenders off the text.
            height: 'auto',
            minHeight: 48,
            paddingTop: 9,
            paddingBottom: 9,
            // room for the Enter hint sitting over the right end of the field
            paddingInlineEnd: 96
        },

        '& .searchbar-input:focus': {
            borderColor: 'var(--ion-color-primary)',
            boxShadow: '0 0 0 3px rgba(17, 120, 157, 0.13)'
        }
    },

    // Says how to escalate from filtering to asking without spending a button on it. Only while the
    // field is empty: once there is text, ion-searchbar draws its clear button in this exact spot.
    enterHint: {
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11.5,
        color: 'var(--ion-color-step-500)',
        pointerEvents: 'none'
    },

    kbd: {
        border: '1px solid var(--ion-color-step-200)',
        borderBottomWidth: 2,
        borderRadius: 5,
        padding: '1px 5px',
        fontSize: 10.5,
        color: 'var(--ion-color-step-650)',
        background: 'var(--ion-color-step-50)'
    },

    examples: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 6,
        padding: '10px 16px 14px'
    },

    example: {
        font: 'inherit',
        fontSize: 12.5,
        lineHeight: 1.3,
        padding: '6px 10px',
        borderRadius: 999,
        border: '1px solid var(--ion-color-step-150)',
        color: 'var(--ion-color-step-700)',
        background: 'var(--ion-color-step-50)',
        textAlign: 'left' as const,
        cursor: 'pointer'
    }
});

export interface IAskBeaCardProps {
    heading: string;
    blurb: string;
    placeholder: string;
    examples: string[];
    /** What asking does. The card never decides this for itself. */
    onAsk: (question: string) => void;
    /**
     * Held by the caller when the text has a second job, as it does on the caseload page where the
     * same string narrows the client roster as it is typed. Left out, the card keeps its own.
     */
    query?: string;
    onQueryChange?: (query: string) => void;
    /** Suppresses the examples and the Enter hint while a question is being answered. */
    isRunning?: boolean;
    /** Anything the caller renders under the field, inside the form's own padding. */
    children?: ReactNode;
    /**
     * Content that belongs at the card's full width rather than inside the field's padding. The
     * results list and its coverage footer go here: nested in the form they pick up 12px of inset on
     * each side, and the footer's divider stops short of the card edge and reads as a mis-drawn rule.
     */
    footer?: ReactNode;
}

/**
 * The ask box, with none of the answering.
 *
 * Split out because two surfaces want the same chrome and completely different behavior behind it:
 * the caseload page puts the question to every client in turn and renders the results here, while
 * the insights tab hands it to Bea's chat. Sharing the state-bound component instead meant sharing
 * one container singleton, so a question typed on one tab silently filtered the other and a search
 * started on one appeared, mid-flight, on the other.
 */
export const AskBeaCard: FC<IAskBeaCardProps> = ({
    heading, blurb, placeholder, examples, onAsk, query, onQueryChange, isRunning, children, footer
}) => {
    const classes = useStyles();
    const [ownQuery, setOwnQuery] = useState('');

    const value = query ?? ownQuery;
    const setValue = onQueryChange ?? setOwnQuery;
    const trimmed = value.trim();

    const ask = (asked: string) => {
        const question = asked.trim();
        if (!question || isRunning) {
            return;
        }
        onAsk(question);
    };

    return (
        <div className={classes.section}>
            <Card data-type="ask-bea-card">
                <div className={classes.header}>
                    <img src="/assets/images/help/helper-bea.png" alt="Bea" className={classes.beaAvatar} />
                    <div>
                        <Text><b>{heading}</b></Text>
                        <div className={classes.subhead}>{blurb}</div>
                    </div>
                </div>

                <div className={classes.form}>
                    <div className={classes.fieldWrap}>
                        <IonSearchbar
                            className={classes.search}
                            data-id="ask-bea-question"
                            value={value}
                            debounce={0}
                            disabled={isRunning}
                            placeholder={placeholder}
                            onIonInput={event => setValue(event.detail.value || '')}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    ask(value);
                                }
                            }}
                        />
                        {!isRunning && trimmed.length === 0 && (
                            <span className={classes.enterHint}>
                                <span className={classes.kbd}>Enter</span> to ask
                            </span>
                        )}
                    </div>

                    {children}
                </div>

                {footer}

                {!isRunning && trimmed.length === 0 && (
                    <div className={classes.examples}>
                        {examples.map(example => (
                            <button
                                key={example}
                                type="button"
                                className={classes.example}
                                data-type="ask-bea-example"
                                onClick={() => ask(example)}
                            >
                                {example}
                            </button>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
};
